/**
 * AfaPlanPreviewTable — Live-AfA-Plan-Vorschau im Wizard
 *
 * Zeigt eine Tabelle: Jahr · AfA-Betrag · Buchwert · Hinweis (Pro-rata/Halbjahres)
 * Holt den Plan vom Backend via /api/fixed-assets/preview-plan.
 */
import { useEffect, useState } from "react";
import api from "@/utils/api";
import { Loader2, Calculator } from "lucide-react";

export default function AfaPlanPreviewTable({
  costNet,
  country,
  method,
  years,
  startDate,
  residual = 0,
  degressiveRate = null,
  privateUsePercentage = 0,
  apiClient = api,
  previewUrl = "/fixed-assets/preview-plan",
}) {
  const [plan, setPlan] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!costNet || !years || !startDate) {
      setPlan([]);
      return;
    }
    setLoading(true);
    const ctl = new AbortController();
    apiClient
      .post(previewUrl, {
        cost_net: costNet,
        country,
        method,
        years,
        start_date: startDate,
        residual,
        degressive_rate: degressiveRate,
        private_use_percentage: privateUsePercentage,
      }, { signal: ctl.signal })
      .then((res) => {
        setPlan(res.data.plan || []);
        setTotal(res.data.total_afa || 0);
      })
      .catch(() => setPlan([]))
      .finally(() => setLoading(false));
    return () => ctl.abort();
  }, [costNet, country, method, years, startDate, residual, degressiveRate, privateUsePercentage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-500" data-testid="afa-plan-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Plan wird berechnet...
      </div>
    );
  }
  if (!plan.length) return null;

  return (
    <div className="border border-emerald-200 rounded-lg overflow-hidden" data-testid="afa-plan-preview">
      <div className="bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 flex items-center gap-2">
        <Calculator className="h-4 w-4" /> AfA-Plan-Vorschau
        <span className="ml-auto text-xs font-normal text-emerald-700">Summe: {total.toFixed(2)} €</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-600">
          <tr>
            <th className="px-3 py-1.5 text-left">Jahr</th>
            <th className="px-3 py-1.5 text-right">AfA</th>
            {privateUsePercentage > 0 && (
              <th className="px-3 py-1.5 text-right" title="Nach Privat-Anteil-Kürzung">Absetzbar</th>
            )}
            <th className="px-3 py-1.5 text-right">Buchwert</th>
            <th className="px-3 py-1.5 text-left">Hinweis</th>
          </tr>
        </thead>
        <tbody>
          {plan.map((p) => (
            <tr key={p.fiscal_year} className="border-t border-slate-100" data-testid={`afa-plan-row-${p.fiscal_year}`}>
              <td className="px-3 py-1.5 font-medium text-slate-700">{p.fiscal_year}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{p.afa_amount.toFixed(2)} €</td>
              {privateUsePercentage > 0 && (
                <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{p.deductible_amount.toFixed(2)} €</td>
              )}
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{p.book_value_after.toFixed(2)} €</td>
              <td className="px-3 py-1.5 text-xs text-slate-500">{p.note || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
